import { Endpoint, Entity } from '@square/ignition';
import {
  OneTrustCategory,
  getLocalStorageItem,
  setLocalStorageItem,
} from '@square/onetrust-compliant-access';
import {
  ApiExplorerInput,
  ApiExplorerInputType,
  ApiExplorerObjectInput,
} from '@squareup/dex-types-shared-api-explorer';
import { isObject } from '@squareup/dex-utils-shared-json';
import { useMemo } from 'react';

import {
  convertApiExplorerMapInputToJsonMap,
  convertJsonMapInputToApiExplorerMapInput,
} from './api-explorer-map';
import {
  isAnyTypeDataField,
  isTypeDataFieldForType,
} from './api-explorer-sort';

const API_EXPLORER_INPUT_PARAMETERS_OBJECT_KEY =
  'api-explorer-input-parameters-object';

const DEFAULT_API_EXPLORER_INPUT: ApiExplorerInput = {
  bodyParameters: {},
  queryParameters: {},
  pathParameters: {},
  authParameters: {},
  fileParameters: {},
};

const generateDefaultInput = () => {
  return structuredClone(DEFAULT_API_EXPLORER_INPUT);
};

const isValidApiExplorerInput = (input: unknown): input is ApiExplorerInput => {
  const inputAsApiExplorerInput = input as ApiExplorerInput;
  return (
    isObject(inputAsApiExplorerInput?.bodyParameters) &&
    isObject(inputAsApiExplorerInput?.queryParameters) &&
    isObject(inputAsApiExplorerInput?.pathParameters) &&
    isObject(inputAsApiExplorerInput?.authParameters) &&
    isObject(inputAsApiExplorerInput?.fileParameters)
  );
};

const filterTypeDataFieldsForExternal = (node: ApiExplorerObjectInput) => {
  const newNode = { ...node };

  // We need to filter out all the _data fields that are not ${type}_data if type exists.
  // This is because, to the user, these fields don't actually exist. So we will remove them when
  // moving it externally
  if (newNode['type'] && typeof newNode['type'] === 'string') {
    const type = newNode['type'];

    const propertyNames = Object.keys(newNode);
    // Check that type matches at least 1 field
    if (!propertyNames.some((name) => isTypeDataFieldForType(name, type))) {
      return newNode;
    }

    // Filter all other _data fields out
    for (const key of Object.keys(newNode)) {
      if (isAnyTypeDataField(key) && !isTypeDataFieldForType(key, type)) {
        delete newNode[key];
      }
    }
  }

  return newNode;
};

const walkInternalApiExplorerInputForExternal = (
  input: ApiExplorerObjectInput,
  entity?: Entity
): ApiExplorerInputType => {
  if (!entity || !input) {
    return input;
  }

  const clonedInput = structuredClone(input);

  const newNode: ApiExplorerObjectInput = {};
  for (const key of Object.keys(clonedInput)) {
    const property = entity.propertyMap.get(key);
    if (!property) {
      continue;
    }

    if (property.isMap) {
      newNode[key] = convertApiExplorerMapInputToJsonMap(
        property,
        clonedInput[key],
        walkInternalApiExplorerInputForExternal
      );
    } else if (property.isArray) {
      if (property.containsEntity) {
        const inputVal = clonedInput[key];
        if (Array.isArray(inputVal)) {
          newNode[key] = inputVal.map((item) =>
            walkInternalApiExplorerInputForExternal(item, property.itemEntity)
          );
        }
      } else {
        newNode[key] = clonedInput[key];
      }
    } else if (property.isAny || property.isFreeformObject) {
      // Special case, return if the value is Any type or
      // a freeform object
      return clonedInput;
    } else if (property.isEntity) {
      newNode[key] = walkInternalApiExplorerInputForExternal(
        clonedInput[key] as ApiExplorerObjectInput,
        property.entity
      );
    } else {
      newNode[key] = clonedInput[key];
    }
  }

  return filterTypeDataFieldsForExternal(newNode);
};

/**
 * A function that will transform a given ApiExplorerInput
 * into a format that can be sent to an external API or code generator.
 * Specifically, it will transform map input from a tuple array to a JSON object.
 * @param input The internal ApiExplorerInput used to generate field values
 * @param endpoint The current endpoint that the input is associated with
 * @returns A transformed ApiExplorerInput that can be used externally.
 */
const transformInputForExternal = (
  input: ApiExplorerInput,
  endpoint?: Endpoint
): ApiExplorerInput => {
  return {
    ...input,
    bodyParameters: walkInternalApiExplorerInputForExternal(
      input.bodyParameters,
      endpoint?.requestBody
    ),
  } as ApiExplorerInput;
};

/**
 * A memoized hook that will transform a given ApiExplorerInput
 * into a format that can be sent to an external API or code generator.
 * Specifically, it will transform map input from a tuple array to a JSON object.
 * @param input The internal ApiExplorerInput used to generate field values
 * @param endpoint The current endpoint that the input is associated with
 * @returns A transformed ApiExplorerInput that can be used externally.
 */
const useTransformInputForExternal = (
  input: ApiExplorerInput,
  endpoint?: Endpoint
) => {
  return useMemo(
    () => transformInputForExternal(input, endpoint),
    [input, endpoint]
  );
};

/**
 * A function that will transform a given external unknown input
 * into a format that can be handled internally by the API Explorer.
 * Specifically, it will transform map input from a JSON object to a tuple array
 * @param input The external input used to generate field values. It may come from local storage or query params
 * @param endpoint The current endpoint that the input is associated with
 * @returns A transformed ApiExplorerInput that can be used internally.
 */
const transformInputFromExternalForInternal = (
  input: ApiExplorerInput | unknown,
  endpoint?: Endpoint
): ApiExplorerInput => {
  if (!input || !isValidApiExplorerInput(input)) {
    return generateDefaultInput();
  }

  if (!endpoint) {
    return input;
  }

  return {
    ...input,
    bodyParameters: convertJsonMapInputToApiExplorerMapInput(
      input.bodyParameters,
      endpoint.requestBody
    ),
  };
};

const getDefaultApiExplorerInput = (
  endpointId: string | undefined
): ApiExplorerInput => {
  let defaultInput = generateDefaultInput();

  if (!endpointId) {
    return defaultInput;
  }

  const retainedInput = getLocalStorageItem(
    API_EXPLORER_INPUT_PARAMETERS_OBJECT_KEY
  );

  if (retainedInput) {
    try {
      const parsedRetainedInputs: Record<string, ApiExplorerInput> = JSON.parse(
        retainedInput as string
      );

      if (parsedRetainedInputs[endpointId]) {
        defaultInput = parsedRetainedInputs[endpointId] as ApiExplorerInput;
      }
    } catch {
      // Eat the parser error and assume its not JSON format
      // Fallback to default input
    }
  }

  return defaultInput;
};

const persistApiExplorerInputToStorage = (
  endpoint: Endpoint | undefined,
  value: ApiExplorerInput
) => {
  if (!endpoint?.id) {
    return;
  }

  let retainedInput: Record<string, ApiExplorerInput> = {};
  try {
    const storedInput = getLocalStorageItem(
      API_EXPLORER_INPUT_PARAMETERS_OBJECT_KEY
    );
    if (storedInput) {
      retainedInput = JSON.parse(storedInput as string);
    }
  } catch {
    // Eat the parser error and assume its not JSON format
    // Try to remediate by setting the value to an empty object
    retainedInput = {};
  }

  retainedInput[endpoint.id] = value;
  setLocalStorageItem(
    API_EXPLORER_INPUT_PARAMETERS_OBJECT_KEY,
    JSON.stringify(retainedInput),
    OneTrustCategory.FUNCTIONAL
  );
};

export {
  generateDefaultInput,
  getDefaultApiExplorerInput,
  persistApiExplorerInputToStorage,
  API_EXPLORER_INPUT_PARAMETERS_OBJECT_KEY,
  useTransformInputForExternal,
  transformInputForExternal,
  transformInputFromExternalForInternal,
};
